(() => {
  class SmsLogoGrid extends HTMLElement {
    connectedCallback() {
      this.swiperContainer = this.querySelector('.swiper');

      if (!this.swiperContainer || this.swiper) {
        return;
      }

      this.initializeWhenReady = this.initializeWhenReady.bind(this);

      if (typeof window.Swiper === 'function') {
        this.initialize();
      } else {
        window.addEventListener('load', this.initializeWhenReady, {
          once: true
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener(
        'load',
        this.initializeWhenReady
      );

      if (this.controlButton && this.onControlClick) {
        this.controlButton.removeEventListener(
          'click',
          this.onControlClick
        );
      }

      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }

      this.swiper = null;
    }

    initializeWhenReady() {
      if (typeof window.Swiper === 'function') {
        this.initialize();
      } else {
        console.error(
          'Logo Grid: swiper-bundle.min.js did not load.'
        );
      }
    }

    initialize() {
      if (this.swiper || !this.swiperContainer) {
        return;
      }

      const slideCount =
        this.swiperContainer.querySelectorAll(
          '.swiper-slide'
        ).length;

      if (slideCount === 0) {
        return;
      }

      const slidesSmallMobile = this.getNumber(
        'slidesSmallMobile',
        3
      );

      const slidesMobile = this.getNumber(
        'slidesMobile',
        4
      );

      const slidesTablet = this.getNumber(
        'slidesTablet',
        5
      );

      const slidesDesktop = this.getNumber(
        'slidesDesktop',
        6
      );

      const gapMobile = this.getNumber('gapMobile', 24);
      const gapDesktop = this.getNumber('gapDesktop', 30);
      const speed = this.getNumber('transitionSpeed', 600);
      const autoplayDelay = this.getNumber(
        'autoplayDelay',
        5000
      );

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      const autoplayEnabled =
        this.getBoolean('autoplay') &&
        !prefersReducedMotion &&
        slideCount > 1;

      /*
       * Recent Swiper versions require enough original slides
       * to support the largest slidesPerView value when looping.
       * When there are not enough, rewind provides a clean fallback.
       */
      const loopEnabled =
        this.getBoolean('loop') &&
        slideCount > 1;

      if (loopEnabled) {
        const minimumSlides = Math.max(
          slidesDesktop * 3,
          12
        );

        const originalSlides = Array.from(
          this.swiperContainer.querySelectorAll('.swiper-slide')
        );

        let cloneIndex = 0;

        while (
          this.swiperContainer.querySelectorAll('.swiper-slide').length <
          minimumSlides
        ) {
          const original =
            originalSlides[cloneIndex % originalSlides.length];

          const clone = original.cloneNode(true);

          clone.removeAttribute('data-shopify-editor-block');
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.logoGridClone = 'true';

          const link = clone.querySelector('a');

          if (link) {
            link.setAttribute('tabindex', '-1');
          }

          this.swiperContainer
            .querySelector('.swiper-wrapper')
            .appendChild(clone);

          cloneIndex++;
        }
      }

      this.swiper = new window.Swiper(
        this.swiperContainer,
        {
          a11y: {
            enabled: true
          },

          autoplay: autoplayEnabled
            ? {
                delay: autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter:
                  this.getBoolean('pauseOnHover')
              }
            : false,

          centeredSlides:
            this.getBoolean('centeredSlides'),
          loop: loopEnabled,
          loopAdditionalSlides: slidesDesktop,
          rewind: false,
          speed,
          slidesPerView: slidesSmallMobile,
          spaceBetween: gapMobile,
          watchOverflow: true,
          observer: true,
          observeParents: true,

          breakpoints: {
            480: {
              slidesPerView: slidesMobile,
              spaceBetween: gapMobile
            },
            750: {
              slidesPerView: slidesTablet,
              spaceBetween: gapMobile
            },
            990: {
              slidesPerView: slidesDesktop,
              spaceBetween: gapDesktop
            }
          },

          on: {
            autoplayStart: () => {
              this.updateControl(false);
            },
            autoplayStop: () => {
              this.updateControl(true);
            }
          }
        }
      );

      this.setupAutoplayControl(autoplayEnabled);
    }

    setupAutoplayControl(autoplayEnabled) {
      this.controlButton = this.querySelector(
        '[data-logo-grid-autoplay-control]'
      );

      if (!this.controlButton) {
        return;
      }

      if (
        !autoplayEnabled ||
        !this.swiper ||
        !this.swiper.autoplay
      ) {
        this.controlButton.hidden = true;
        return;
      }

      this.controlButton.hidden = false;

      this.onControlClick = () => {
        const isPaused =
          this.controlButton.getAttribute(
            'aria-pressed'
          ) === 'true';

        if (isPaused) {
          this.swiper.autoplay.start();
          this.updateControl(false);
        } else {
          this.swiper.autoplay.stop();
          this.updateControl(true);
        }
      };

      this.controlButton.addEventListener(
        'click',
        this.onControlClick
      );
    }

    updateControl(isPaused) {
      if (!this.controlButton) {
        return;
      }

      const label = isPaused
        ? 'Play logo carousel'
        : 'Pause logo carousel';

      this.controlButton.setAttribute(
        'aria-pressed',
        String(isPaused)
      );

      this.controlButton.setAttribute(
        'aria-label',
        label
      );

      const text = this.controlButton.querySelector(
        '[data-logo-grid-control-text]'
      );

      if (text) {
        text.textContent = label;
      }
    }

    getBoolean(name) {
      return this.dataset[name] === 'true';
    }

    getNumber(name, fallback) {
      const value = Number(this.dataset[name]);

      return Number.isFinite(value)
        ? value
        : fallback;
    }
  }

  if (!customElements.get('sms-logo-grid')) {
    customElements.define(
      'sms-logo-grid',
      SmsLogoGrid
    );
  }
})();